import { useCallback } from "react";

const RECENTLY_VISITED_KEY = "recentlyVisited";
const PER_TYPE_MAX = 10;

export type RecentType = "people" | "jobs" | "facilities" | "sections" | "events" | "activities" | "other";

export function inferTypeByUrl(url: string): RecentType {
  if (url.startsWith("/people/")) return "people";
  if (url.startsWith("/jobs/")) return "jobs";
  if (url.startsWith("/facilities/")) return "facilities";
  if (url.startsWith("/sections/")) return "sections";
  if (url.startsWith("/events/") || url.startsWith("/activities/events/")) return "events";
  if (url.startsWith("/activities/")) return "activities";
  return "other";
}

export function matchesRecentFilter(itemType: RecentType, filter: "all" | RecentType): boolean {
  if (filter === "all") return true;
  if (filter === "activities") return itemType === "activities" || itemType === "events";
  return itemType === filter;
}

function normalizeByType(items: RecentlyVisitedItem[]): RecentlyVisitedItem[] {
  // Group by inferred type
  const groups: Record<RecentType, RecentlyVisitedItem[]> = {
    people: [],
    jobs: [],
    facilities: [],
    sections: [],
    events: [],
    activities: [],
    other: []
  };

  for (const item of items) {
    const type = (item.type as RecentType) || "other";
    const normalizedItem: RecentlyVisitedItem = { ...item, type };
    groups[type].push(normalizedItem);
  }

  // Sort each group by dateTime desc and slice to PER_TYPE_MAX for known types
  const sortDesc = (a: RecentlyVisitedItem, b: RecentlyVisitedItem) =>
    new Date(b.dateTime as unknown as string).getTime() - new Date(a.dateTime as unknown as string).getTime();

  groups.people.sort(sortDesc);
  groups.jobs.sort(sortDesc);
  groups.facilities.sort(sortDesc);
  groups.sections.sort(sortDesc);
  groups.events.sort(sortDesc);
  groups.activities.sort(sortDesc);
  groups.other.sort(sortDesc);

  const merged = [
    ...groups.people.slice(0, PER_TYPE_MAX),
    ...groups.jobs.slice(0, PER_TYPE_MAX),
    ...groups.facilities.slice(0, PER_TYPE_MAX),
    ...groups.sections.slice(0, PER_TYPE_MAX),
    ...groups.events.slice(0, PER_TYPE_MAX),
    ...groups.activities.slice(0, PER_TYPE_MAX),
    ...groups.other.slice(0, PER_TYPE_MAX)
  ];

  // Final sort by dateTime desc for All view
  return merged.sort(sortDesc);
}

export type RecentlyVisitedItem = {
  url: string;
  text: string;
  subtext: string;
  image: string;
  dateTime: Date;
  type?: RecentType;
};

function readRecentlyVisitedFromStorage(): RecentlyVisitedItem[] {
  const raw = localStorage.getItem(RECENTLY_VISITED_KEY);
  if (!raw) {
    return [];
  }

  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as RecentlyVisitedItem[]) : [];
  } catch {
    return [];
  }
}

export function useRecentlyVisited() {
  const getRecentlyVisitedItems = useCallback((): RecentlyVisitedItem[] => {
    return normalizeByType(readRecentlyVisitedFromStorage());
  }, []);

  const addRecentlyVisitedItem = useCallback((item: Omit<RecentlyVisitedItem, "dateTime">) => {
    const existing = readRecentlyVisitedFromStorage();
    const computedType = item.type ?? inferTypeByUrl(item.url);
    const newItem: RecentlyVisitedItem = { ...item, type: computedType, dateTime: new Date() as unknown as Date };

    // De-dupe by URL, then normalize per-type limits
    const deDuped = [newItem, ...existing.filter((i) => i.url !== item.url)];
    const normalized = normalizeByType(deDuped);

    localStorage.setItem(RECENTLY_VISITED_KEY, JSON.stringify(normalized));
  }, []);

  const removeRecentlyVisitedItem = useCallback((url: string) => {
    const existing = readRecentlyVisitedFromStorage();
    const filtered = existing.filter((item) => item.url !== url);
    localStorage.setItem(RECENTLY_VISITED_KEY, JSON.stringify(filtered));
  }, []);

  return { getRecentlyVisitedItems, addRecentlyVisitedItem, removeRecentlyVisitedItem };
}
